<template>
  <WdsAlert v-if="networkErrors && Object.keys(networkErrors).length" error class="tw-mb-2">
    <ul class="tw-mb-0">
      <li v-for="networkError in networkErrors" :key="networkError">{{ networkError }}</li>
    </ul>
  </WdsAlert>

  <WdsStack direction-column gap-md align-center class="tw-pb-7.5 tw--mt-8">
    <WdsAvatar xl :path="getAvatarPath(jobCandidateWorkerAvatar, jobCandidateWorkerId)" />

    <WdsStack direction-column gap-xs align-center>
      <WdsText xl primary>{{ jobCandidateWorkerName }}</WdsText>

      <WdsText l tertiary>
        {{ jobCandidateWorkerTitle }}
      </WdsText>
    </WdsStack>
  </WdsStack>

  <WdsStack direction-column gap-lg>
    <WdsStack direction-column gap-md>
      <WdsAlert warning bordered>
        <WdsStack gap-md class="tw-items-center">
          <WdsIcon name="locked" :size="16" />
          <div>
            {{ $t('Private to your company') }}
          </div>
        </WdsStack>
      </WdsAlert>

      <WdsSelect
        v-model="removalReason"
        :label="$t('Reason')"
        :placeholder="$t('Select the reason')"
        :list="JobCandidateStatusReasonListArray"
        :error="validationErrors['input.statusReason']"
        data-test="candidate-removal-reason"
      >
        <template #option="{ option }">
          <div :data-test="`candidate-removal-reason-${option.label.toLowerCase()}`">
            {{ option.label }}
          </div>
        </template>
      </WdsSelect>

      <WdsInputArea
        v-model="removalComment"
        :label="$t('Comment')"
        :placeholder="$t('What influenced this decision...')"
        :error="validationErrors['input.statusComment']"
        height="70px"
        data-test="candidate-removal-comment"
      />
    </WdsStack>

    <WdsDivider />

    <WdsStack v-if="offer?.id" direction-column gap-md>
      <WdsAlert info bordered>
        <WdsStack gap-md class="tw-items-center">
          <WdsIcon name="chat" :size="16" />
          <div>
            {{ $t('Added to your conversation with') }}
            {{ offer.supplier ? offer.supplier.name : offer.worker.name }}
          </div>
        </WdsStack>
      </WdsAlert>

      <WdsInputArea
        v-model="feedback"
        :label="$t('Share feedback')"
        :placeholder="$t('What would you like to share...')"
        :error="validationErrors['input.feedback']"
        height="70px"
        data-test="candidate-removal-feedback"
      />

      <WdsDivider />
    </WdsStack>

    <WdsStack direction-row justify-end gap-sm>
      <WdsButton tertiary data-test="candidate-removal-cancel" @click="emit('cancel')">
        {{ $t('Back') }}
      </WdsButton>

      <WdsButton
        error
        class="tw-items-center tw-gap-sm"
        :disabled="updateJobCandidateStatusLoading"
        data-test="candidate-removal-remove"
        @click="handleRemoveCandidate"
      >
        <WdsSpinner v-if="updateJobCandidateStatusLoading" indeterminate />
        {{ $t('Remove candidate') }}
      </WdsButton>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsAlert,
  WdsSelect,
  WdsInputArea,
  WdsButton,
  WdsSpinner,
  WdsStack,
  WdsAvatar,
  WdsIcon,
  WdsText,
  WdsDivider,
} from '@worksome/wds-components'
import { ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { JobCandidateStatusReasonListArray } from '@/components/Job/constants'
import { JobCandidateStatus, JobCandidateStatusReason } from '@/gql/graphql'
import { UpdateJobCandidateStatusMutation } from '@/components/Job/Candidates/graphql/mutations'
import useApolloError from '@/composables/useApolloError'
import { getAvatarPath } from '@/utils/avatar'

const props = defineProps({
  companyId: {
    type: String,
    required: true,
  },
  jobId: {
    type: String,
    required: true,
  },
  jobCandidateId: {
    type: String,
    required: true,
  },
  jobCandidateWorkerId: {
    type: String,
    required: true,
  },
  jobCandidateWorkerName: {
    type: String,
    required: true,
  },
  jobCandidateWorkerTitle: {
    type: String,
    required: true,
  },
  jobCandidateWorkerAvatar: {
    type: String,
    required: true,
  },
  offer: {
    type: Object,
    required: false,
    default: undefined,
  },
})

const emit = defineEmits(['cancel', 'candidate-updated'])

const removalReason = ref<{ label: string; value: JobCandidateStatusReason } | null>(null)
const removalComment = ref<string | undefined>(undefined)
const feedback = ref<string | undefined>(undefined)

const {
  loading: updateJobCandidateStatusLoading,
  mutate: mutateUpdateJobCandidateStatus,
  error: errorUpdateJobCandidateStatus,
} = useMutation(UpdateJobCandidateStatusMutation, {
  clientId: 'publicApi',
})

const handleRemoveCandidate = async () => {
  await mutateUpdateJobCandidateStatus({
    input: {
      jobCandidate: props.jobCandidateId,
      status: JobCandidateStatus.NonEligible,
      statusReason: removalReason.value?.value,
      statusComment: removalComment.value ?? null,
      feedback: feedback.value ?? null,
    },
  }).then(() => {
    emit('candidate-updated')
  })
}

const { networkErrors, validationErrors } = useApolloError(errorUpdateJobCandidateStatus)
</script>
